import { DATE_FORMATS_FNS } from "components";
import { setUsersDashboardFilters, useGetUsersDashboardFilters } from "modules/UsersDashboard/components/UsersDashboardFilters/hooks";
import { USER_STATUSES, USER_TYPES } from "modules/UsersDashboard/hooks";
import { USERS_DASHBOARD_STORAGE_KEYS } from "modules/UsersDashboard/storageKeys";
import { PAGE_SIZE, setPreference } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

export const useUsersDashboardFilterHandlers = () => {
  const { filters } = useGetUsersDashboardFilters();

  const handleResetFilters = () => {
    setPreference(USERS_DASHBOARD_STORAGE_KEYS.ActiveDateFromPreference, null);
    setPreference(USERS_DASHBOARD_STORAGE_KEYS.ActiveDateToPreference, null);
    setPreference(USERS_DASHBOARD_STORAGE_KEYS.CombiUserPreference, null);
    setPreference(USERS_DASHBOARD_STORAGE_KEYS.DPOPreference, null);
    setPreference(USERS_DASHBOARD_STORAGE_KEYS.MobileVersionsPreference, null);
    setPreference(USERS_DASHBOARD_STORAGE_KEYS.RolesPreference, []);
    setPreference(USERS_DASHBOARD_STORAGE_KEYS.StatusPreference, USER_STATUSES.Active);
    setPreference(USERS_DASHBOARD_STORAGE_KEYS.WebVersionPreference, null);
    setPreference(USERS_DASHBOARD_STORAGE_KEYS.PageSizePreference, PAGE_SIZE.PageSize25);

    setUsersDashboardFilters({
      ...filters,
      active_date_from: null,
      active_date_to: null,
      dealer_location_ids: [],
      dealer_ids: [],
      status: USER_STATUSES.Active,
      is_combi_user: null,
      is_dpo: null,
      mobile_versions: null,
      page_size: PAGE_SIZE.PageSize25,
      roles: [],
      term: "",
      web_versions: null,
      page: 1
    });
  };

  const handleSearchChange = (value: string) => setUsersDashboardFilters({ ...filters, term: value });

  const handleActiveDateFromChange = (activeDateFrom: Date | null) => {
    const active_date_from = activeDateFrom ? formatDate(activeDateFrom, DATE_FORMATS_FNS.ISOFormatEndOfDay) : null;

    setPreference(USERS_DASHBOARD_STORAGE_KEYS.ActiveDateFromPreference, active_date_from);
    setUsersDashboardFilters({ ...filters, active_date_from, page: 1 });
  };

  const handleActiveDateToChange = (activeDateTo: Date | null) => {
    const active_date_to = activeDateTo ? formatDate(activeDateTo, DATE_FORMATS_FNS.ISOFormatEndOfDay) : null;

    setPreference(USERS_DASHBOARD_STORAGE_KEYS.ActiveDateToPreference, active_date_to);
    setUsersDashboardFilters({ ...filters, active_date_to, page: 1 });
  };

  const handleCombiUsersChange = (is_combi_user: boolean | null) => {
    setPreference(USERS_DASHBOARD_STORAGE_KEYS.CombiUserPreference, is_combi_user);
    setUsersDashboardFilters({ ...filters, is_combi_user, page: 1 });
  };

  const handleDPOChange = (is_dpo: boolean | null) => {
    setPreference(USERS_DASHBOARD_STORAGE_KEYS.DPOPreference, is_dpo);
    setUsersDashboardFilters({ ...filters, is_dpo, page: 1 });
  };

  const handleAppVersionsChange = (mobile_versions: string[]) => {
    setPreference(USERS_DASHBOARD_STORAGE_KEYS.MobileVersionsPreference, mobile_versions);
    setUsersDashboardFilters({ ...filters, mobile_versions, page: 1 });
  };

  const handleWebVersionsChange = (web_versions: string[]) => {
    setPreference(USERS_DASHBOARD_STORAGE_KEYS.WebVersionPreference, web_versions);
    setUsersDashboardFilters({ ...filters, web_versions, page: 1 });
  };

  const handleUserRolesChange = (roles: number[]) => {
    setPreference(USERS_DASHBOARD_STORAGE_KEYS.RolesPreference, roles);
    setUsersDashboardFilters({ ...filters, roles, page: 1 });
  };

  const handleUserTypeChange = (status: USER_TYPES) => {
    if (status === USER_TYPES.All) {
      setPreference(USERS_DASHBOARD_STORAGE_KEYS.CombiUserPreference, null);
      setPreference(USERS_DASHBOARD_STORAGE_KEYS.DPOPreference, null);
      setUsersDashboardFilters({ ...filters, is_combi_user: null, is_dpo: null, page: 1 });
    }
    if (status === USER_TYPES.Combi) {
      setPreference(USERS_DASHBOARD_STORAGE_KEYS.CombiUserPreference, true);
      setPreference(USERS_DASHBOARD_STORAGE_KEYS.DPOPreference, null);
      setUsersDashboardFilters({ ...filters, is_combi_user: true, is_dpo: null, page: 1 });
    }
    if (status === USER_TYPES.Regular) {
      setPreference(USERS_DASHBOARD_STORAGE_KEYS.CombiUserPreference, false);
      setPreference(USERS_DASHBOARD_STORAGE_KEYS.DPOPreference, null);
      setUsersDashboardFilters({ ...filters, is_combi_user: false, is_dpo: null, page: 1 });
    }
    if (status === USER_TYPES.Dpo) {
      setPreference(USERS_DASHBOARD_STORAGE_KEYS.CombiUserPreference, null);
      setPreference(USERS_DASHBOARD_STORAGE_KEYS.DPOPreference, true);
      setUsersDashboardFilters({ ...filters, is_combi_user: null, is_dpo: true, page: 1 });
    }
  };

  const handleUserStatusChange = (status: USER_STATUSES) => {
    setPreference(USERS_DASHBOARD_STORAGE_KEYS.StatusPreference, status);
    setUsersDashboardFilters({ ...filters, status, page: 1 });
  };

  const handleDealerChange = (dealerId: number | null) =>
    setUsersDashboardFilters({ ...filters, dealer_ids: dealerId ? [dealerId] : [], dealer_location_ids: [], page: 1 });
  const handleLocationChange = (dealerLocationIds: number[] | null) => setUsersDashboardFilters({ ...filters, dealer_location_ids: dealerLocationIds ?? [], page: 1 });

  return {
    handleResetFilters,
    handleSearchChange,
    handleActiveDateFromChange,
    handleActiveDateToChange,
    handleCombiUsersChange,
    handleDPOChange,
    handleAppVersionsChange,
    handleWebVersionsChange,
    handleUserRolesChange,
    handleUserTypeChange,
    handleUserStatusChange,
    handleDealerChange,
    handleLocationChange
  };
};
